import type { PrinterConnectionType, PrinterDevice } from './types'

export type PrinterSettings = {
  autoPrint: boolean
  preferredConnection: PrinterConnectionType
  paperWidth: '50mm' | '72mm' | 'auto'
  printer: PrinterDevice | null
  showPreviewBeforePrinting: boolean
}

const STORAGE_KEY = 'eventpay-printer-settings'

const defaultSettings: PrinterSettings = {
  autoPrint: true,
  preferredConnection: 'bluetooth',
  paperWidth: '72mm',
  printer: null,
  showPreviewBeforePrinting: false,
}

const isBrowser = () => typeof window !== 'undefined'

const loadSettings = (): PrinterSettings | null => {
  if (!isBrowser()) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PrinterSettings
    return parsed || null
  } catch (error) {
    console.error('[PrinterSettings] Failed to load settings', error)
    return null
  }
}

const saveSettings = (settings: PrinterSettings) => {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (error) {
    console.error('[PrinterSettings] Failed to save settings', error)
  }
}

export const getPrinterSettings = (): PrinterSettings => {
  const settings = loadSettings()
  return settings || { ...defaultSettings }
}

export const setPrinterSettings = (updates: Partial<PrinterSettings>): PrinterSettings => {
  const current = loadSettings() || { ...defaultSettings }
  const next = { ...current, ...updates }
  saveSettings(next)
  return next
}

export const clearPrinterSettings = () => {
  saveSettings({ ...defaultSettings })
}
